/**
 * The flows worth measuring (#379).
 *
 * ── WHY THERE IS A LIST AT ALL ──
 *
 * app.FeatureUsageAnalytics stores a FUNNEL -- Started, Completed,
 * Abandoned, with a duration -- not a hit counter. That shape only
 * answers a question for work that has a definite finish somebody can
 * fail to reach. "Opened the dashboard" has no completion, so an
 * abandonment rate for it is noise with a percentage sign on it, and a
 * table full of noise is how the three admin surfaces that read it get
 * ignored.
 *
 * So the set is deliberately SMALL and deliberately a list in one file.
 * A free string at each call site would let the names drift -- "inspect"
 * here, "inspection" there -- and GROUP BY feature_name would then
 * report one flow as two, each with half the traffic. The constant is
 * the only thing that makes the admin surface's grouping mean anything.
 *
 * ── WHAT EARNS A PLACE ──
 *
 * Three questions, and a flow has to answer at least one:
 *
 *   1. Did somebody try this and give up? (onboarding, import)
 *   2. Is this slower than it looks? (inspection, bottling)
 *   3. Is this feature used at all, by the accounts paying for it?
 *      (#378's usage-based trial exit needs exactly this.)
 *
 * A one-field form fails all three: abandoning it means somebody tapped
 * Add and changed their mind, which tells us nothing we would act on.
 * That is why logging a feeding and logging a treatment are absent while
 * logging an inspection is here -- the inspection form is the long one,
 * and it is the one a keeper fills in standing in a yard with gloves on.
 *
 * ── THE NAMES ARE DOTTED, AND THAT IS LOAD-BEARING ──
 *
 * `area.task`, so the admin surface can group by prefix later without a
 * migration. feature_name is NVARCHAR(100), which these are well inside.
 *
 * MIRRORS mobile/src/services/flows.js. A flow named differently on the
 * two clients reports as two flows, each with half its traffic, and
 * looks correct on either screen.
 */
export const FLOWS = {
  /* THE EXPENSIVE ONES TO ABANDON. Somebody who gives up here never
     reaches the product at all, so these are the two where an
     abandonment rate is worth more than everything else combined. */
  ONBOARDING: "onboarding.setup",
  /*
   * SKIPPING, COUNTED SEPARATELY (4 Oct).
   *
   * Tapping Skip ends onboarding.setup as ABANDONED, because the
   * question that flow answers is "how many new accounts never got a
   * hive in" and somebody who skipped did not get set up. But
   * "abandoned" then covers two groups with different fixes: people we
   * LOST, and people who will come back to it. A single rate cannot tell
   * you which you are looking at.
   *
   * So a skip also records this, one Completed event per skip -- a
   * counter rather than a funnel, which is why it has no Started. The
   * decomposition on the admin surface is a subtraction:
   *
   *   setup.Abandoned - skipped.Completed = the ones who actually left
   *
   * The alternative was to end setup as Completed on a skip, which would
   * have reported onboarding as near-perfect while those accounts sat
   * empty. The other alternative -- leaving setup with no terminal event
   * at all -- would break the funnel's arithmetic: every Started must
   * get exactly one ending, or Started stops equalling
   * Completed + Abandoned and the surface reads as a bug.
   */
  ONBOARDING_SKIPPED: "onboarding.skipped",
  IMPORT: "import.csv",

  /* THE WORK ITSELF, and the set here is SHORTER than it first looked.
     Logging an inspection is not on it: on both clients that turned out
     to be a single tap -- web's is a batch action over selected hives,
     mobile's is logAction("Inspection") behind one button -- and a flow
     you cannot abandon has no funnel. Guessing it in would have shipped
     a flow whose abandonment rate was structurally zero, which is the
     exact failure #379 warns about.

     What IS here is the work with steps in it: running a checklist (a
     screen you move through and can walk away from) and bottling (the
     one with arithmetic, where "is this slower than it looks" is a real
     question). */
  CHECKLIST: "hive.checklist",
  HIVE_ADD: "hive.add",
  HARVEST: "harvest.log",
  BOTTLING: "pool.bottle",

  /* THE BUSINESS SIDE, which is what the paid tiers are for -- so "is
     this used at all by the accounts paying for it" is the live
     question (#378). */
  SALE: "sale.record",
  EXPENDITURE: "expenditure.record",

  /* TEAM, where an abandonment is somebody deciding not to share their
     apiary after all -- the single most interesting drop-off in the
     product. */
  INVITE: "team.invite",
};

/* Exported for the guard test, which checks that no two flows share a
   name and that nothing calls startFlow with a string that is not in
   here. A typo'd name does not error -- it just quietly becomes an
   eleventh flow with one event in it. */
export const FLOW_NAMES = Object.values(FLOWS);
